import {useEffect, useMemo} from 'react';

import getGameId from './getGameId';
import useLocalStorage from './useLocalStorage';

const gameId = getGameId();

export const LOAD_MODE = {
  MINIMAL: 'minimal',
  LIGHT: 'light',
  FULL: 'full',
} as const;

export type LoadMode = (typeof LOAD_MODE)[keyof typeof LOAD_MODE];

type SharedState = {
  loadMode: LoadMode;
  currentGame: string;
};

interface WebViewMessageType<T extends keyof SharedState> {
  type: T;
  body: SharedState[T];
}

interface WebViewMessageHandler<T extends keyof SharedState> {
  onMessage?: (message: WebViewMessageType<keyof SharedState>) => void;
  filter?: (state: SharedState) => SharedState[T];
  defaultState?: SharedState;
}

export default function useWebviewMessaging<T extends keyof SharedState>({
  onMessage,
  filter,
  defaultState = {
    loadMode: LOAD_MODE.FULL,
    currentGame: gameId || '',
  },
}: WebViewMessageHandler<T>): SharedState[T] | undefined {
  const [sharedState, setSharedState] = useLocalStorage<SharedState>(
    `webview_messaging_state_${gameId}`,
    defaultState
  );
  const filteredState = useMemo(
    () => (filter ? filter(sharedState) : undefined),
    [sharedState, filter]
  );

  useEffect(() => {
    const handleMessage = (event: CustomEvent<WebViewMessageType<keyof SharedState>>) => {
      const message = event.detail;
      setSharedState((prev) => ({...prev, [message.type]: message.body}));
      onMessage?.(message);
    };

    window.addEventListener('webview-message', handleMessage as EventListener);
    return () => {
      window.removeEventListener('webview-message', handleMessage as EventListener);
    };
  }, [onMessage, setSharedState]);

  return filteredState;
}
